import { Component, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { AccountView, SearchContactsForAccountParameters } from '@api-proxies';
import { ServerSideGridComponent } from '@components/shared/grids/server-side-grid/server-side-grid.component';
import { ContactService } from '@http/clients/contact.service';
import { GridService } from '@services/grid.service';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ACCOUNT_CONTACTS_GRID_COLUMNS } from './accounts-single-contacts.config';

@Component({
    selector: 'app-accounts-single-contacts',
    templateUrl: './accounts-single-contacts.component.html',
    styleUrls: ['./accounts-single-contacts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent]
})
export class AccountsSingleContactsComponent {
    account = input<AccountView>();

    tabCounterChange = output<void>();
    accountChange = output<void>();

    contactsDatasource?: IServerSideDatasource;
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    gridColumns: ColDef[] = [...ACCOUNT_CONTACTS_GRID_COLUMNS];

    private readonly contactService = inject(ContactService);
    private readonly gridService = inject(GridService);

    constructor() {
        effect(() => {
            const accountId = this.account()?.id;
            if (accountId && !this.contactsDatasource) {
                this.createDatasource();
            }
        });
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private searchParamsBuilder(gridParams: any): SearchContactsForAccountParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;
        const accountId = this.account()?.id ?? 0;

        if (!accountId) {
            throw new Error('Account ID is required to search contacts.');
        }

        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            accountId
        } as SearchContactsForAccountParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.contactsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.contactService.searchContactsForAccount(searchParams);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching contacts:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }
}
